/**
 * Safe number utilities to prevent NaN values from reaching CoreGraphics APIs
 * These utils prevent the blank WebView issue on iOS caused by NaN calculations
 */

/**
 * Ensures a value is a finite number, with fallback
 * @param v - Value to check
 * @param fallback - Fallback value if not finite (default: 0)
 * @returns Safe finite number
 */
export const num = (v: any, fallback = 0): number => {
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Safe number with logging when fallback is used (for debugging)
 * @param label - Label for debugging
 * @param v - Value to check  
 * @param fallback - Fallback value
 * @returns Safe finite number
 */
export const safeNum = (label: string, v: any, fallback = 0): number => {
  const parsed = Number(v);
  const result = Number.isFinite(parsed) ? parsed : fallback;
  
  if (result === fallback && v !== fallback && process.env.NODE_ENV === 'development') {
    console.warn(`[safeNum] Fallback used for ${label}:`, v, '→', result);
  }
  
  return result;
};

/**
 * Safe division to prevent NaN and division by zero
 * @param numerator - Number to divide
 * @param denominator - Number to divide by
 * @param fallback - Fallback if division would be unsafe
 * @returns Safe division result
 */
export const safeDiv = (numerator: any, denominator: any, fallback = 0): number => {
  const n = num(numerator, 0);
  const d = num(denominator, 1);
  
  if (d === 0) return fallback;
  
  const result = n / d;
  return Number.isFinite(result) ? result : fallback;
};

/**
 * Safe aspect ratio calculation
 * @param width - Width value
 * @param height - Height value  
 * @param fallback - Fallback ratio (default: 1)
 * @returns Safe aspect ratio
 */
export const safeAspectRatio = (width: any, height: any, fallback = 1): number => {
  const w = Math.max(1, num(width, 1));
  const h = Math.max(1, num(height, 1));
  return safeDiv(h, w, fallback);
};

/**
 * Safe canvas dimensions
 * @param width - Canvas width
 * @param height - Canvas height
 * @returns Safe canvas dimensions (minimum 1x1)
 */
export const safeCanvasDimensions = (width: any, height: any) => ({
  width: Math.max(1, num(width, 1)),
  height: Math.max(1, num(height, 1))
});

/**
 * Safe transform values for CSS
 * @param x - X translation
 * @param y - Y translation
 * @param scale - Scale value
 * @returns Safe transform object
 */
export const safeTransform = (x: any = 0, y: any = 0, scale: any = 1) => ({
  x: num(x, 0),
  y: num(y, 0),
  scale: Math.max(0.01, num(scale, 1)) // Prevent zero scale
});

/**
 * Safe audio progress calculation
 * @param currentTime - Current playback time
 * @param duration - Total duration
 * @returns Safe progress value (0-1)
 */
export const safeAudioProgress = (currentTime: any, duration: any): number => {
  const current = Math.max(0, num(currentTime, 0));
  const total = Math.max(0.001, num(duration, 0.001)); // Prevent division by zero
  return Math.min(1, current / total);
};